import { ConfigFormHeader } from '@/components/ConfigFormHeader'
import { useDealership } from '@/contexts/DealershipContext'
import { getConfiguration, saveConfiguration } from '@/services/configuration.api'
import { ConversationFlowConfig, InventoryPricingConfig } from '@/types/configuration'
import { Card, Checkbox, NumberInput, Stack, Text } from '@mantine/core'
import { useForm } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import { useEffect } from 'react'

type DiscoveryFormValues = Pick<
  ConversationFlowConfig,
  'askForBudget' | 'askForTradeIn' | 'purchaseTimeline' | 'financingPreferences'
> & {
  maxVehiclesToSuggest: number
}

interface DiscoveryFormProps {
  data: ConversationFlowConfig | null
  onSave: (data: ConversationFlowConfig) => void
  isSaving: boolean
}

export function DiscoveryForm({ data, onSave, isSaving }: DiscoveryFormProps) {
  const { selectedEntity } = useDealership()
  const dealershipId = selectedEntity?.id || ''

  const form = useForm<DiscoveryFormValues>({
    initialValues: {
      askForBudget: true,
      askForTradeIn: false,
      purchaseTimeline: true,
      financingPreferences: false,
      maxVehiclesToSuggest: 3,
    },
  })

  useEffect(() => {
    if (!data) {
      return
    }
    form.setFieldValue('askForBudget', data.askForBudget ?? true)
    form.setFieldValue('askForTradeIn', data.askForTradeIn ?? false)
    form.setFieldValue('purchaseTimeline', data.purchaseTimeline ?? true)
    form.setFieldValue('financingPreferences', data.financingPreferences ?? false)
  }, [data])

  useEffect(() => {
    if (!dealershipId) {
      return
    }
    const load = async () => {
      const res = await getConfiguration<InventoryPricingConfig>(dealershipId, 'inventory_pricing')
      if (res.success && res.data && typeof res.data.maxVehiclesToSuggest === 'number') {
        form.setFieldValue('maxVehiclesToSuggest', res.data.maxVehiclesToSuggest)
      }
    }
    void load()
  }, [dealershipId])

  const handleSubmit = async (values: DiscoveryFormValues) => {
    if (!dealershipId) {
      return
    }

    const { maxVehiclesToSuggest, ...goals } = values

    const flowPayload: ConversationFlowConfig = {
      ...goals,
      appointmentSuggestion: data?.appointmentSuggestion ?? 'flexible',
    }

    await onSave(flowPayload)

    const invRes = await saveConfiguration<InventoryPricingConfig>(dealershipId, 'inventory_pricing', {
      maxVehiclesToSuggest,
    })
    if (!invRes.success) {
      notifications.show({
        title: 'Error',
        message: invRes.error || 'Failed to save vehicle suggestion limit',
        color: 'red',
      })
    }
  }

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="md">
        <ConfigFormHeader onReset={() => form.reset()} isSaving={isSaving} />
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Stack gap="md">
            <Text fw={600}>Discovery Goals</Text>
            <Checkbox
              label="Ask for Budget"
              description={'Example: "What\'s your budget range?"'}
              {...form.getInputProps('askForBudget', { type: 'checkbox' })}
            />
            <Checkbox
              label="Ask for Trade-in"
              description={'Example: "Do you have a vehicle to trade in?"'}
              {...form.getInputProps('askForTradeIn', { type: 'checkbox' })}
            />
            <Checkbox
              label="Purchase Timeline"
              description={'Example: "When are you looking to buy?"'}
              {...form.getInputProps('purchaseTimeline', { type: 'checkbox' })}
            />
            <Checkbox
              label="Financing Preferences"
              description={'Example: "How would you like to finance?"'}
              {...form.getInputProps('financingPreferences', { type: 'checkbox' })}
            />
          </Stack>
        </Card>

        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Stack gap="md">
            <Text fw={600}>Vehicle Suggestion</Text>
            <NumberInput
              label="Max vehicles to suggest"
              description="Limit how many vehicle options the AI may suggest in a given context."
              min={1}
              max={10}
              {...form.getInputProps('maxVehiclesToSuggest')}
            />
          </Stack>
        </Card>
      </Stack>
    </form>
  )
}
